import { useState, useEffect, useMemo, FC, Suspense } from 'react';
import AthleteEffortDetailLazy from '../components/effort/AthleteEffortDetailLazy';
import { Link, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useModuleVisibility } from '../../hooks/useModuleVisibility';
import { useData } from '../context/DataContext';
import { useQuery } from '@tanstack/react-query';
import AthleteReadinessView from '../components/AthleteReadinessView';
import AthleteTestingViewTable from '../components/AthleteTestingViewTable';
import ProfilingHubContent from '../../pages/ProfilingHubPage/ProfilingHubContent';
import AthleteSettingsModal from '../components/AthleteSettingsModal';
import RTPDashboard from '../components/RTPDashboard';
import TestFilterModal from '../components/TestFilterModal';
import AthleteSubjectiveReadiness from '../components/AthleteSubjectiveReadiness';
import DateNavigator from '../components/DateNavigator';
import { useInjuryEndpoints } from '../../hooks/useInjuryEndpoints';
import { IInjury } from '../../models/injury';
import { InjuryBadge } from '../../shared/InjuryBadge/InjuryBadge';
import { CertificateBadge } from '../../shared/CertificateBadge/CertificateBadge';
import { getCertificateState } from '../../models/injury';
import { INJURY_BODY_ZONE, INJURY_SEVERITY } from '../../enums/injuries';
import { InjuryRegistrationModal } from '../components/InjuryRegistrationForm/InjuryRegistrationModal';
import { ShowByRole } from '../../shared/ShowByRole';
import { AnamnesisCard } from '../components/AnamnesisCard';
import { InjuryTimeline } from '../components/InjuryTimeline/InjuryTimeline';
import { AthleteInjuryCharts } from '../components/AthleteInjuryCharts/AthleteInjuryCharts';
import { InsightsCard } from '../components/AthleteInjuryCharts/InsightsCard';
import { ChartErrorState } from '../components/AthleteInjuryCharts/charts/_shared/ChartErrorState';
import { ChartEmptyState } from '../components/AthleteInjuryCharts/charts/_shared/ChartEmptyState';
import { IInsightsMetric } from '../../models/injury';
import { ATHLETE_INJURY_METRIC } from '../../enums/injuries';
import { BandaidsIcon } from '@phosphor-icons/react';
import { Table as TableIcon, BarChart3, Settings2, Dumbbell } from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import svgPaths from '../imports/svg-pahk28nbs7';
import imgAvatar2 from '../assets/0e37c1b462f8f944b04a84e37f107ab8f5342ab0.png';
import styles from './PlayerDetail.module.scss';
import dayjs from 'dayjs';
import { useEndpoints } from '../../hooks/useEndpoints';
import ProgrammingTab from '../../pages/UserDetailPage/ProgrammingTab/ProgrammingTab';
import { CarichiAthleteSection } from '../../pages/UserDetailPage/components/CarichiAthleteSection';
import AthleteBodyMap from '../components/AthleteBodyMap/AthleteBodyMap';
import AthleteBodyMapHistoryView from '../components/AthleteBodyMapHistoryView/AthleteBodyMapHistoryView';
import { BodyMapWeeklyWidget } from '../components/BodyMapWeeklyWidget/BodyMapWeeklyWidget';
import { BodyMapTimeline } from '../components/BodyMapTimeline/BodyMapTimeline';
import BodyMapMonthCalendar from '../components/BodyMapMonthCalendar/BodyMapMonthCalendar';
import CustomButton from '../../shared/CustomButton/CustomButton';
import InjuryPeriodFilter, { InjuryPeriod } from '../../shared/InjuryPeriodFilter/InjuryPeriodFilter';
import LeopartLoader from '../../shared/LeopartLoader/LeopartLoader';

interface PlayerDetailProps {
  playerId: string;
  playerName?: string;
  playerPosition?: string;
  playerTeam?: string;
  avatarSrc?: string;
}

interface UserDetails {
  user: Record<string, any>;
  recovery: { current: number; previous: number; delta: number };
  readiness: {
    subjective: { current: number; previous: number; delta: number };
    objective: { current: number; previous: number; delta: number };
  };
  effort: { current: number; previous: number; delta: number };
  stress: { current: number; previous: number; delta: number };
  charts: {
    recovery: Array<{ date: string; recovery: number; subjectiveRecovery: number; objectiveRecovery: number }>;
    effort: Array<{ date: string; value: number }>;
    stress: Array<{ date: string; value: number }>;
  };
  events: {
    past: Array<{
      _id: string;
      startDate?: string;
      endDate?: string;
      date?: string;
      type: string;
      name?: string;
      testType?: string;
      location?: string;
      duration?: number;
      completed: boolean;
      compiledIds: string[];
    }>;
    upcoming: Array<{
      _id: string;
      startDate?: string;
      endDate?: string;
      date?: string;
      type: string;
      name?: string;
      testType?: string;
      location?: string;
      duration?: number;
      completed: boolean;
      compiledIds: string[];
    }>;
  };
}

function CaretLeft() {
  return (
    <div className={styles.iconSm}>
      <svg className={styles.iconSvg} fill="none" preserveAspectRatio="none" viewBox="0 0 16 16">
        <g id="CaretLeft">
          <path d="M10 12L6 8L10 4" stroke="#00212C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </g>
      </svg>
    </div>
  );
}

function MapPin() {
  return (
    <div className={styles.iconSm}>
      <svg className={styles.iconSvg} fill="none" preserveAspectRatio="none" viewBox="0 0 16 16">
        <g id="MapPin">
          <path d={svgPaths.p3a1dfa80} fill="var(--fill-0, #00212C)" stroke="var(--stroke-0, #00212C)" strokeWidth="0.0625" />
        </g>
      </svg>
    </div>
  );
}

function SlidersHorizontal() {
  return (
    <div className={styles.iconSm}>
      <svg className={styles.iconSvg} fill="none" preserveAspectRatio="none" viewBox="0 0 16 16">
        <g id="SlidersHorizontal">
          <path d={svgPaths.p20d5f240} fill="var(--fill-0, #4D4D4D)" stroke="var(--stroke-0, #4D4D4D)" strokeWidth="0.0625" />
        </g>
      </svg>
    </div>
  );
}

function formatXDate(dateStr: string) {
  return dayjs(dateStr).format('DD/MM');
}

function formatEventDate(dateStr: string | undefined) {
  if (!dateStr) return '—';
  return dayjs(dateStr).format('DD MMM');
}

function formatEventTime(dateStr: string | undefined) {
  if (!dateStr) return '—';
  return dayjs(dateStr).format('HH:mm');
}

function mapTestType(testType: string | undefined) {
  if (!testType) return '—';
  const map: Record<string, string> = {
    'cmj': 'CMJ',
    'drop-jump': 'Drop Jump',
    'drop_jump': 'Drop Jump',
    'rpe': 'RPE',
  };
  return map[testType] ?? testType;
}

function getEventName(event: UserDetails['events']['past'][0]) {
  if (event.type === 'test') return mapTestType(event.testType);
  return event.name ?? '—';
}

const EVENT_BADGE_COLORS: Record<string, { bg: string; color: string }> = {
  workout: { bg: '#e8f4fd', color: '#1565c0' },
  test: { bg: '#e8fdf0', color: '#1b7a3e' },
  match: { bg: '#fde8e8', color: '#b71c1c' },
};

function EventBadge({ type }: { type: string }) {
  const colors = EVENT_BADGE_COLORS[type] ?? { bg: '#f6f6f6', color: 'var(--c-text-1)' };
  return (
    <span
      style={{
        backgroundColor: colors.bg,
        color: colors.color,
        padding: '2px 8px',
        borderRadius: '9999px',
        fontSize: '11px',
        fontWeight: 700,
        fontFamily: "'Satoshi', sans-serif",
        textTransform: 'capitalize',
      }}
    >
      {type}
    </span>
  );
}

function DeltaChip({ delta, unit = '', inverted = false }: { delta: number; unit?: string; inverted?: boolean }) {
  const positive = inverted ? delta <= 0 : delta >= 0;
  const color = positive ? '#15803d' : '#b91c1c';
  const bg = positive ? '#dcfce7' : '#fee2e2';
  const arrow = positive ? '▲' : '▼';
  return (
    <span
      style={{
        backgroundColor: bg,
        color,
        padding: '2px 8px',
        borderRadius: '9999px',
        fontSize: '12px',
        fontWeight: 700,
        fontFamily: "'Satoshi', sans-serif",
      }}
    >
      {arrow} {Math.abs(delta).toFixed(1)}{unit}
    </span>
  );
}

function LoadingSpinner() {
  return (
    <div className={styles.spinnerWrapper}>
      <div className={styles.spinner} />
    </div>
  );
}

// Mappa tab-id → chiave visibilità
const PLAYER_TAB_VISIBILITY_KEYS: Record<string, string> = {
  'panoramica': 'player-detail-panoramica',
  'statistiche': 'player-detail-statistiche',
  'attivita': 'player-detail-attivita',
  'readiness': 'player-detail-readiness',
  'testing': 'player-detail-testing',
  'questionario': 'player-detail-questionario',
  'programmazione': 'player-detail-programmazione',
  'storico-clinico': 'player-detail-storico-clinico',
  'fastidi': 'player-detail-fastidi',
  'carichi': 'player-detail-carichi',
  'sforzo': 'player-detail-sforzo',
};

export const PlayerDetail: FC<PlayerDetailProps> = ({ playerId, playerName, playerPosition, playerTeam, avatarSrc }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isTabVisible } = useModuleVisibility();
  type TabId = 'panoramica' | 'statistiche' | 'attivita' | 'readiness' | 'testing' | 'questionario' | 'programmazione' | 'storico-clinico' | 'fastidi' | 'carichi' | 'sforzo';
  const ALL_TABS: readonly TabId[] = ['panoramica', 'statistiche', 'attivita', 'readiness', 'testing', 'questionario', 'programmazione', 'storico-clinico', 'fastidi', 'carichi', 'sforzo'];
  const VALID_TABS = ALL_TABS.filter((t) => isTabVisible(PLAYER_TAB_VISIBILITY_KEYS[t]));
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const t = searchParams.get('tab');
    return t && (ALL_TABS as readonly string[]).includes(t) ? (t as TabId) : 'readiness';
  });

  const changeTab = (tab: TabId) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: false });
  };

  // Se il tab attivo viene nascosto da un flag, spostati al primo visibile
  useEffect(() => {
    if (VALID_TABS.length > 0 && !VALID_TABS.includes(activeTab)) {
      changeTab(VALID_TABS[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [VALID_TABS.join(','), activeTab]);

  const VALID_TESTING_MODES = ['entry', 'analytics', 'profiling'] as const;
  type TestingMode = typeof VALID_TESTING_MODES[number];
  const [testingViewMode, setTestingViewMode] = useState<TestingMode>(() => {
    const m = searchParams.get('testingMode');
    return m && (VALID_TESTING_MODES as readonly string[]).includes(m) ? (m as TestingMode) : 'entry';
  });

  const changeTestingMode = (mode: TestingMode) => {
    setTestingViewMode(mode);
    const next = new URLSearchParams(searchParams);
    next.set('testingMode', mode);
    setSearchParams(next, { replace: true });
  };
  const [selectedDate, setSelectedDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isTestFilterOpen, setIsTestFilterOpen] = useState(false);
  const [isInjuryModalOpen, setIsInjuryModalOpen] = useState(false);
  const [prefillInjuryData, setPrefillInjuryData] = useState<{
    bodyZone?: string;
    injuryDate?: string;
    description?: string;
    sourceBodyMapComplaintId?: string;
  } | null>(null);
  const [hiddenTestIds, setHiddenTestIds] = useState<Set<string>>(new Set());
  const [details, setDetails] = useState<UserDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [injuryPeriod, setInjuryPeriod] = useState<InjuryPeriod>('6m');
  const [fastidiDate, setFastidiDate] = useState<string>(dayjs().format('YYYY-MM-DD'));

  const endpoints = useEndpoints();
  const injuryEndpoints = useInjuryEndpoints();
  const { performanceTestResults } = useData();

  // Spec sez.7.4: il profilo atleta mostra i test EFFETTUATI, non l'intero catalogo (1147).
  const doneTestIds = useMemo(() => {
    const s = new Set<string>();
    for (const r of performanceTestResults) {
      if (r.athleteId === playerId) s.add(r.testId);
    }
    return s;
  }, [performanceTestResults, playerId]);
  const visibleDoneCount = useMemo(
    () => [...doneTestIds].filter((id) => !hiddenTestIds.has(id)).length,
    [doneTestIds, hiddenTestIds],
  );

  // Infortuni attivi dell'atleta
  const { data: athleteInjuries } = useQuery<IInjury[]>({
    queryKey: ['athlete-injuries', playerId],
    queryFn: async () => {
      const { data } = await injuryEndpoints.getAthleteInjuries(playerId);
      return data;
    },
    enabled: !!playerId,
    staleTime: 1000 * 60 * 5,
  });

  const activeInjury = (athleteInjuries ?? []).find((inj) => inj.isActive) ?? null;

  // teamId dell'atleta per registrare un infortunio dal profilo.
  // La route è `users/:id` (senza teamId): fonte primaria il virtual `teams` popolato da
  // GET /users/:id/details (fix backend), con fallback al teamId di un infortunio esistente
  // (nel Team Space il teamId è sempre garantito). Vedi bug b3e591.
  const athleteTeamId: string =
    (details?.user as any)?.teams?.[0]?._id ??
    (details?.user as any)?.teams?.[0] ??
    activeInjury?.teamId ??
    (athleteInjuries ?? [])[0]?.teamId ??
    '';

  const { data: insightsData, isLoading: insightsLoading, isError: insightsError, error: insightsErrorObj, refetch: insightsRefetch } = useQuery<IInsightsMetric>({
    queryKey: ['injury', 'athlete', playerId, 'metric', ATHLETE_INJURY_METRIC.INSIGHTS, injuryPeriod],
    queryFn: async () => {
      const { data } = await injuryEndpoints.getAthleteInjuryMetric(
        playerId,
        ATHLETE_INJURY_METRIC.INSIGHTS,
        { period: injuryPeriod }
      );
      return data as IInsightsMetric;
    },
    enabled: !!playerId && activeTab === 'storico-clinico',
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && (VALID_TABS as readonly string[]).includes(tab) && tab !== activeTab) {
      setActiveTab(tab as TabId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Apre il modal infortuni pre-compilato quando si naviga da "Apri infortunio" in AlertsDropdown
  useEffect(() => {
    const state = location.state as any;
    if (state?.prefillInjury) {
      setPrefillInjuryData(state.prefillInjury);
      setIsInjuryModalOpen(true);
      // Rimuove lo state dalla history per evitare ri-aperture
      window.history.replaceState({}, document.title, window.location.href);
    }
  }, [location.state]);

  useEffect(() => {
    if (!playerId) return;
    setDetailsLoading(true);
    endpoints.getUserDetails(playerId)
      .then((res) => {
        setDetails(res.data);
      })
      .catch((err) => {
        console.error('Error fetching user details:', err);
      })
      .finally(() => {
        setDetailsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId]);

  /* ─── Tab: Panoramica ─── */
  const renderPanoramica = () => {
    if (detailsLoading) return <LoadingSpinner />;
    if (!details) return <div className={styles.emptyState}>Nessun dato disponibile.</div>;

    const kpis = [
      {
        label: 'Recovery',
        value: details.recovery.current,
        max: 100,
        delta: details.recovery.delta,
        unit: '',
      },
      {
        label: 'Readiness Sogg.',
        value: details.readiness.subjective.current,
        max: 5,
        delta: details.readiness.subjective.delta,
        unit: '',
      },
      {
        label: 'RPE',
        value: details.effort.current,
        max: 10,
        delta: details.effort.delta,
        unit: '',
        inverted: true,
      },
      {
        label: 'Stress',
        value: details.stress.current,
        max: 5,
        delta: details.stress.delta,
        unit: '',
        inverted: true,
      },
    ];

    const recoveryData = (details.charts.recovery ?? []).map((d) => ({
      ...d,
      dateLabel: formatXDate(d.date),
    }));

    const upcomingEvents = (details.events.upcoming ?? []).slice(0, 5);

    return (
      <div className={styles.tabContent}>
        {/* KPI Row */}
        <div className={styles.kpiRow}>
          {kpis.map((kpi) => (
            <div key={kpi.label} className={styles.kpiCard}>
              <p className={styles.kpiLabel}>{kpi.label}</p>
              <p className={styles.kpiValue}>
                {/* BUG-AUDIT-025 fix: kpi.value può essere string (es. 'medium')
                    o number. Crash su .toFixed se string. Coerce numerico o fallback. */}
                {typeof kpi.value === 'number' && !isNaN(kpi.value)
                  ? kpi.value.toFixed(1)
                  : kpi.value != null && !isNaN(Number(kpi.value))
                    ? Number(kpi.value).toFixed(1)
                    : kpi.value != null
                      ? String(kpi.value)
                      : '—'}
                <span className={styles.kpiMax}> / {kpi.max}</span>
              </p>
              <DeltaChip delta={kpi.delta} unit={kpi.unit} inverted={(kpi as any).inverted} />
            </div>
          ))}
        </div>

        {/* Recovery trend + upcoming events */}
        <div className={styles.panRow}>
          {/* Recovery chart */}
          <div className={styles.chartCard}>
            <p className={styles.chartTitle}>Trend Recovery</p>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={recoveryData} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="recoveryGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#013344" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#013344" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e7e7" />
                <XAxis dataKey="dateLabel" tick={{ fontSize: 11, fontFamily: 'Satoshi, sans-serif', fill: '#4d4d4d' }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fontFamily: 'Satoshi, sans-serif', fill: '#4d4d4d' }} />
                <Tooltip
                  contentStyle={{ fontFamily: 'Satoshi, sans-serif', fontSize: 12, borderRadius: 8, border: '1px solid #e7e7e7' }}
                />
                <Area
                  type="monotone"
                  dataKey="recovery"
                  name="Recovery"
                  stroke="#013344"
                  strokeWidth={2}
                  fill="url(#recoveryGrad)"
                />
                <Line
                  type="monotone"
                  dataKey="subjectiveRecovery"
                  name="Sogg."
                  stroke="#ffd15a"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="objectiveRecovery"
                  name="Ogg."
                  stroke="#4CAF50"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  dot={false}
                />
                <Legend wrapperStyle={{ fontSize: 12, fontFamily: 'Satoshi, sans-serif' }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Upcoming events */}
          <div className={styles.upcomingCard}>
            <p className={styles.chartTitle}>Prossimi eventi</p>
            {upcomingEvents.length === 0 ? (
              <p className={styles.emptyText}>Nessun evento in programma.</p>
            ) : (
              <div className={styles.eventList}>
                {upcomingEvents.map((ev) => {
                  const evDate = ev.startDate ?? ev.date;
                  return (
                    <div key={ev._id} className={styles.eventItem}>
                      <div className={styles.eventDateBlock}>
                        <span className={styles.eventDay}>{formatEventDate(evDate)}</span>
                        <span className={styles.eventTime}>{formatEventTime(evDate)}</span>
                      </div>
                      <div className={styles.eventInfo}>
                        <EventBadge type={ev.type} />
                        <span className={styles.eventName}>{getEventName(ev)}</span>
                        {ev.location && (
                          <span className={styles.eventLocation}>{ev.location}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ─── Tab: Statistiche ─── */
  const renderStatistiche = () => {
    if (detailsLoading) return <LoadingSpinner />;
    if (!details) return <div className={styles.emptyState}>Nessun dato disponibile.</div>;

    const effortData = (details.charts.effort ?? []).map((d) => ({
      ...d,
      dateLabel: formatXDate(d.date),
    }));

    const stressData = (details.charts.stress ?? []).map((d) => ({
      ...d,
      dateLabel: formatXDate(d.date),
    }));

    const recoveryData = (details.charts.recovery ?? []).map((d) => ({
      ...d,
      dateLabel: formatXDate(d.date),
    }));

    const commonTickStyle = { fontSize: 11, fontFamily: 'Satoshi, sans-serif', fill: '#4d4d4d' };
    const tooltipStyle = { fontFamily: 'Satoshi, sans-serif', fontSize: 12, borderRadius: 8, border: '1px solid #e7e7e7' };

    return (
      <div className={styles.tabContent}>
        {/* Effort chart — sRPE (RPE × durata min) */}
        <div className={styles.chartCard}>
          <p className={styles.chartTitle}>Carico di lavoro (sRPE)</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={effortData} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e7e7" />
              <XAxis dataKey="dateLabel" tick={commonTickStyle} />
              <YAxis domain={[0, 'auto']} tick={commonTickStyle} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="value" name="sRPE" fill="#ffd15a" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Stress chart — stressLevel Hooper (1-5) */}
        <div className={styles.chartCard}>
          <p className={styles.chartTitle}>Livello di Stress (1–5)</p>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={stressData} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="stressGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ff6b6b" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#ff6b6b" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e7e7" />
              <XAxis dataKey="dateLabel" tick={commonTickStyle} />
              <YAxis domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} tick={commonTickStyle} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="value" name="Stress" stroke="#ff6b6b" strokeWidth={2} fill="url(#stressGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Recovery sogg vs ogg */}
        <div className={styles.chartCard}>
          <p className={styles.chartTitle}>Recovery Soggettiva vs Oggettiva</p>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={recoveryData} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e7e7" />
              <XAxis dataKey="dateLabel" tick={commonTickStyle} />
              <YAxis domain={[0, 100]} tick={commonTickStyle} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12, fontFamily: 'Satoshi, sans-serif' }} />
              <Line
                type="monotone"
                dataKey="subjectiveRecovery"
                name="Soggettiva"
                stroke="#4CAF50"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="objectiveRecovery"
                name="Oggettiva"
                stroke="#2196F3"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  /* ─── Tab: Attività ─── */
  const renderAttivita = () => {
    if (detailsLoading) return <LoadingSpinner />;
    if (!details) return <div className={styles.emptyState}>Nessun dato disponibile.</div>;

    const upcoming = details.events.upcoming ?? [];
    const past = details.events.past ?? [];

    const renderEventTable = (
      events: UserDetails['events']['past'],
      showStatus: boolean,
      isPast: boolean
    ) => {
      if (events.length === 0) {
        return <p className={styles.emptyText}>Nessun evento.</p>;
      }

      return (
        <div className={styles.eventTable}>
          <div className={styles.eventTableHeader}>
            <span>Data</span>
            <span>Tipo</span>
            <span>Nome</span>
            <span>Orario</span>
            {showStatus && <span>Stato</span>}
          </div>
          {events.map((ev) => {
            const evDate = ev.startDate ?? ev.date;
            const isCompiled = ev.compiledIds?.includes(playerId);
            return (
              <div key={ev._id} className={styles.eventTableRow}>
                <span className={styles.eventTableDate}>{formatEventDate(evDate)}</span>
                <span><EventBadge type={ev.type} /></span>
                <span className={styles.eventTableName}>{getEventName(ev)}</span>
                <span className={styles.eventTableTime}>{formatEventTime(evDate)}</span>
                {showStatus && (
                  <span>
                    {isPast ? (
                      isCompiled ? (
                        <span className={styles.statusCompiled}>Completato</span>
                      ) : (
                        <span className={styles.statusMissing}>Non compilato</span>
                      )
                    ) : (
                      <span className={styles.statusPending}>Da compilare</span>
                    )}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      );
    };

    return (
      <div className={styles.tabContent}>
        {/* Upcoming */}
        <div className={styles.chartCard}>
          <p className={styles.chartTitle}>Prossimi eventi</p>
          {renderEventTable(upcoming, true, false)}
        </div>

        {/* Past */}
        <div className={styles.chartCard}>
          <p className={styles.chartTitle}>Eventi passati</p>
          {renderEventTable(past, true, true)}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.scrollArea}>
        <div className={styles.inner}>
          {/* Back button */}
          <Link to="/" className={styles.backLink}>
            <CaretLeft />
            <p className={styles.backLinkText}>Vai al dettaglio squadra</p>
          </Link>

          {/* Player Header */}
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <div className={styles.avatarWrapper}>
                <img alt="" className={styles.avatarImg} src={avatarSrc ?? imgAvatar2} />
                <div className={styles.avatarBorder} />
              </div>
              <div>
                <p className={styles.playerName}>{playerName ?? '—'}</p>
                <div className={styles.playerMeta}>
                  {playerPosition && <span className={styles.playerPosition}>{playerPosition}</span>}
                  {playerPosition && playerTeam && <span className={styles.playerSeparator}>•</span>}
                  {playerTeam && (
                    <div className={styles.playerTeamRow}>
                      <MapPin />
                      <span className={styles.playerTeam}>{playerTeam}</span>
                    </div>
                  )}
                  {playerTeam && <span className={styles.playerSeparator}>•</span>}
                  {activeInjury ? (
                    <InjuryBadge
                      injury={{
                        injuryId: activeInjury._id,
                        athleteId: playerId,
                        bodyZone: activeInjury.bodyZone as INJURY_BODY_ZONE,
                        daysOut: Math.floor((Date.now() - new Date(activeInjury.injuryDate).getTime()) / 86400000),
                        severity: (activeInjury.severityEstimated ?? activeInjury.severity) as INJURY_SEVERITY,
                        rtpPhase: activeInjury.rtpProtocolId ? undefined : undefined,
                        expectedReturnDate: activeInjury.expectedReturnDate,
                      }}
                    />
                  ) : (
                    <span className={styles.statusBadge}>Attivo</span>
                  )}
                  {/* Certificate badge — visibile se il certificato è in scadenza o scaduto */}
                  {getCertificateState((details?.user as any)?.medicalCertificateExpiry) !== 'valid' &&
                    getCertificateState((details?.user as any)?.medicalCertificateExpiry) !== 'missing' && (
                      <CertificateBadge
                        certificate={{ medicalCertificateExpiry: (details?.user as any)?.medicalCertificateExpiry }}
                      />
                    )}
                </div>
              </div>
            </div>

            <div className={styles.headerActions}>
              <ShowByRole roles={['isDirector', 'isCoach']}>
                <div title={!activeInjury && !athleteTeamId ? 'Atleta non associato a nessun team' : undefined}>
                  <CustomButton
                    size="sm"
                    color={activeInjury ? 'error' : 'outline'}
                    icon={<BandaidsIcon size={16} />}
                    iconPosition="left"
                    disabled={!activeInjury && !athleteTeamId}
                    onClick={() => setIsInjuryModalOpen(true)}
                  >
                    {activeInjury ? 'Modifica infortunio' : 'Registra infortunio'}
                  </CustomButton>
                </div>
              </ShowByRole>
              <button className={styles.settingsBtn} onClick={() => setIsSettingsModalOpen(true)}>
                <SlidersHorizontal />
                <p className={styles.settingsBtnLabel}>Impostazioni</p>
              </button>
            </div>
          </div>

          {/* Tabs and Date */}
          <div className={styles.tabsRow}>
            <div className={styles.tabsLeft}>
              {/* Tab Group */}
              <div className={styles.tabGroup}>
                {VALID_TABS.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => changeTab(tab)}
                    className={[styles.tabBtn, activeTab === tab ? styles.tabBtnActive : ''].filter(Boolean).join(' ')}
                  >
                    <div className={styles.tabBtnInner}>
                      <p className={[styles.tabLabel, activeTab === tab ? styles.tabLabelActive : ''].filter(Boolean).join(' ')}>
                        {tab === 'questionario' ? 'Questionario soggettivo'
                          : tab === 'programmazione' ? 'Programmazione'
                          : tab === 'storico-clinico' ? 'Storico clinico'
                          : tab === 'fastidi' ? 'Fastidi'
                          : tab === 'carichi' ? 'Carichi'
                          : tab === 'sforzo' ? 'Sforzo'
                          : tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            {/* Right side: DateNavigator for Readiness, Data Entry/Analytics toggle for Testing */}
            {activeTab === 'readiness' ? (
              <DateNavigator selectedDate={selectedDate} onDateChange={setSelectedDate} />
            ) : activeTab === 'testing' ? (
              <div className={styles.viewToggle}>
                <button
                  onClick={() => setIsTestFilterOpen(true)}
                  className={styles.viewToggleBtn}
                  title="Filtra test"
                >
                  <Settings2 className={styles.viewToggleIcon} />
                  <span>Filtra test ({visibleDoneCount}/{doneTestIds.size})</span>
                </button>
                <button
                  onClick={() => changeTestingMode('entry')}
                  className={[styles.viewToggleBtn, testingViewMode === 'entry' ? styles.viewToggleBtnActive : ''].filter(Boolean).join(' ')}
                >
                  <TableIcon className={styles.viewToggleIcon} />
                  <span>Dati</span>
                </button>
                <button
                  onClick={() => changeTestingMode('analytics')}
                  className={[styles.viewToggleBtn, testingViewMode === 'analytics' ? styles.viewToggleBtnActive : ''].filter(Boolean).join(' ')}
                >
                  <BarChart3 className={styles.viewToggleIcon} />
                  <span>Analitiche</span>
                </button>
                <button
                  onClick={() => changeTestingMode('profiling')}
                  className={[styles.viewToggleBtn, testingViewMode === 'profiling' ? styles.viewToggleBtnActive : ''].filter(Boolean).join(' ')}
                >
                  <Dumbbell className={styles.viewToggleIcon} />
                  <span>Profiling</span>
                </button>
              </div>
            ) : null}
          </div>

          {/* Conditional Content Based on Active Tab */}
          {activeTab === 'readiness' ? (
            <AthleteReadinessView athleteId={playerId!} selectedDate={selectedDate} />
          ) : activeTab === 'testing' ? (
            testingViewMode === 'profiling' ? (
              <div className={styles.tabContent}>
                <ProfilingHubContent athleteId={playerId!} />
              </div>
            ) : (
              <>
                <AthleteTestingViewTable
                  athleteId={playerId!}
                  selectedDate={selectedDate}
                  onDateChange={setSelectedDate}
                  mode={testingViewMode}
                  setMode={changeTestingMode as (mode: 'entry' | 'analytics') => void}
                  hiddenTestIds={hiddenTestIds}
                />
                <div style={{ marginTop: 24 }}>
                  <RTPDashboard athleteId={playerId!} />
                </div>
              </>
            )
          ) : activeTab === 'panoramica' ? (
            renderPanoramica()
          ) : activeTab === 'statistiche' ? (
            renderStatistiche()
          ) : activeTab === 'attivita' ? (
            renderAttivita()
          ) : activeTab === 'questionario' ? (
            <AthleteSubjectiveReadiness athleteId={playerId!} />
          ) : activeTab === 'programmazione' ? (
            <ProgrammingTab athleteId={playerId!} />
          ) : activeTab === 'carichi' ? (
            <CarichiAthleteSection athleteId={playerId!} athleteName={playerName} />
          ) : activeTab === 'sforzo' ? (
            <Suspense fallback={<div className={styles.spinnerWrapper}><div className={styles.spinner} /></div>}>
              <AthleteEffortDetailLazy
                athleteId={playerId!}
                teamId={athleteTeamId}
                athleteName={playerName}
              />
            </Suspense>
          ) : activeTab === 'fastidi' ? (
            <div className={styles.tabContent}>
              <AthleteBodyMap
                athleteId={playerId!}
                selectedDate={fastidiDate}
                onDateChange={setFastidiDate}
              />
              <BodyMapMonthCalendar
                athleteId={playerId!}
                selectedDate={fastidiDate}
                onDateChange={setFastidiDate}
              />
              <AthleteBodyMapHistoryView athleteId={playerId!} />
            </div>
          ) : activeTab === 'storico-clinico' ? (
            <div className={styles.tabContent}>
              {/* Filtro periodo — componente condiviso InjuryPeriodFilter */}
              <div className={styles.periodFilterRow}>
                <span className={styles.periodFilterLabel}>Periodo:</span>
                <InjuryPeriodFilter value={injuryPeriod} onChange={setInjuryPeriod} />
              </div>

              {/* 0. Widget fastidi ultima settimana */}
              <BodyMapWeeklyWidget athleteId={playerId!} />

              {/* 1. Anamnesi */}
              <AnamnesisCard athleteId={playerId!} />

              {/* 2. Insights — loading, error e empty espliciti (mai "Nessun dato" mentre è ancora in fetch) */}
              {insightsLoading ? (
                <div className={styles.insightsPanel}>
                  <LeopartLoader size="sm" />
                </div>
              ) : insightsError ? (
                <ChartErrorState onRetry={insightsRefetch} message={(insightsErrorObj as Error)?.message} />
              ) : insightsData && insightsData.insights.length > 0 ? (
                <InsightsCard insights={insightsData.insights} />
              ) : (
                <ChartEmptyState />
              )}

              {/* 3. Timeline infortuni */}
              <div>
                <div className={styles.sectionHeaderRow}>
                  <span className={styles.sectionTitle}>Infortuni</span>
                  <ShowByRole roles={['isDirector', 'isCoach']}>
                    <div title={!activeInjury && !athleteTeamId ? 'Atleta non associato a nessun team' : undefined}>
                      <CustomButton
                        size="sm"
                        color="secondary"
                        icon={<BandaidsIcon size={14} />}
                        iconPosition="left"
                        disabled={!activeInjury && !athleteTeamId}
                        onClick={() => setIsInjuryModalOpen(true)}
                      >
                        Registra infortunio
                      </CustomButton>
                    </div>
                  </ShowByRole>
                </div>
                <InjuryTimeline
                  athleteId={playerId!}
                  teamId={athleteTeamId}
                  period={injuryPeriod}
                />
              </div>

              {/* 3b. Timeline segnalazioni body map */}
              <BodyMapTimeline
                athleteId={playerId!}
                injuries={athleteInjuries ?? []}
              />

              {/* 4. Grafici condizionali */}
              <AthleteInjuryCharts athleteId={playerId!} period={injuryPeriod} />
            </div>
          ) : null}
        </div>
      </div>

      {/* Settings Modal */}
      <AthleteSettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        athleteId={playerId!}
        athleteName={playerName ?? ''}
      />

      <TestFilterModal
        isOpen={isTestFilterOpen}
        onClose={() => setIsTestFilterOpen(false)}
        hiddenTestIds={hiddenTestIds}
        onApply={setHiddenTestIds}
      />

      <InjuryRegistrationModal
        isOpen={isInjuryModalOpen}
        onClose={() => { setIsInjuryModalOpen(false); setPrefillInjuryData(null); }}
        athleteId={playerId!}
        teamId={athleteTeamId}
        editingInjury={prefillInjuryData ? undefined : (activeInjury ?? undefined)}
        prefillData={prefillInjuryData}
      />
    </div>
  );
}

export default PlayerDetail
